import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { canAccess } from '../utils/canAccess';
import {
  Alert, Button, Card, Col, Form,
  InputGroup, Row, Spinner
} from 'react-bootstrap';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import {
  FaBroom, FaCalendarAlt, FaFileExcel,
  FaReceipt, FaSearch, FaTrash,
  FaFileImport, FaCheckCircle, FaExclamationTriangle
} from 'react-icons/fa';
import '../styles/StcPage.css';

const MOTIF_LABELS = {
  licenciement: 'Licenciement',
  démission:    'Démission',
  fin_chantier: 'Fin de chantier',
  autre:        'Autre'
};

const MOTIF_STYLE = {
  licenciement: { color: '#c62828', bg: '#fff5f5', border: '#ef9a9a', row: '#fff8f8' },
  démission:    { color: '#e65100', bg: '#fff8f0', border: '#ffcc80', row: '#fffbf5' },
  fin_chantier: { color: '#1565c0', bg: '#f0f6ff', border: '#90caf9', row: '#f5f9ff' },
  autre:        { color: '#546e7a', bg: '#f5f7f8', border: '#b0bec5', row: '#f8f9fa' },
};

const fmtDH = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';

const yearStart = `${new Date().getFullYear()}-01-01`;
const today     = new Date().toISOString().split('T')[0];

const StcPage = ({ user }) => {
  const isAdmin  = user?.role === 'admin';
  const canWrite = isAdmin || canAccess(user, 'ouvriers:write');

  const [stcs,      setStcs]      = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const [dateDebut, setDateDebut] = useState(yearStart);
  const [dateFin,   setDateFin]   = useState(today);
  const [searchText, setSearchText] = useState('');
  const [chantierFilter, setChantierFilter] = useState([]);
  const [motifFilter,    setMotifFilter]    = useState('');

  const [sortField, setSortField] = useState('date_stc');
  const [sortDir,   setSortDir]   = useState('desc');

  // Sélection bulk
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [bulkAction,    setBulkAction]    = useState('');    // 'delete'|'motif'|'date'|'recalculate'
  const [bulkMotif,     setBulkMotif]     = useState('');
  const [bulkDate,      setBulkDate]      = useState('');
  const [bulkLoading,   setBulkLoading]   = useState(false);

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleSelectAll = () => {
    if (selectedIds.size === sorted.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sorted.map(r => r.id)));
  };

  const handleBulkAction = async () => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    setError('');
    try {
      const ids = [...selectedIds];
      if (bulkAction === 'delete') {
        if (!window.confirm(`Supprimer ${ids.length} STC ?`)) { setBulkLoading(false); return; }
        await axios.delete('/stc/bulk', { data: { ids } });
        setSuccess(`✅ ${ids.length} STC supprimés.`);
      } else {
        const { data } = await axios.put('/stc/bulk', {
          ids,
          action: bulkAction,
          motif:    bulkMotif  || undefined,
          date_stc: bulkDate   || undefined
        });
        setSuccess(`✅ ${data.updated} STC mis à jour.${data.errors?.length ? ` (${data.errors.length} erreur(s))` : ''}`);
      }
      setSelectedIds(new Set());
      setBulkAction('');
      fetchStcs();
    } catch (err) {
      setError(err.response?.data?.message || '❌ Erreur action de masse.');
    } finally {
      setBulkLoading(false);
    }
  };

  // Import modal
  const [showImport,   setShowImport]   = useState(false);
  const [importRows,   setImportRows]   = useState([]);   // parsed preview
  const [importErrors, setImportErrors] = useState([]);   // backend errors
  const [importing,    setImporting]    = useState(false);
  const [importDone,   setImportDone]   = useState(null); // { imported, errors }
  const importRef = React.useRef(null);

  const fetchStcs = async () => {
    setLoading(true);
    setError('');
    try {
      const [resStcs, resC] = await Promise.all([
        axios.get('/stc', { params: { start: dateDebut, end: dateFin } }),
        axios.get('/chantiers')
      ]);
      setStcs(Array.isArray(resStcs.data) ? resStcs.data : []);
      setChantiers(Array.isArray(resC.data) ? resC.data : []);
    } catch {
      setError('❌ Erreur de chargement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStcs(); }, [dateDebut, dateFin]); // eslint-disable-line react-hooks/exhaustive-deps

  const chantierOptions = useMemo(() => [
    { value: '__ALL__', label: '✅ Tous les chantiers' },
    ...(chantiers || []).map(c => ({ value: c.id, label: c.name }))
  ], [chantiers]);

  const norm = (s = '') =>
    String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

  const filtered = useMemo(() => {
    const txt = norm(searchText);
    return stcs.filter(s => {
      const chantierName = s.ouvrier?.Chantier?.name || '';
      const chantierIdOuvrier = Number(s.ouvrier?.chantier_id || 0);

      if (chantierFilter.length && !chantierFilter.includes(chantierIdOuvrier)) return false;
      if (motifFilter && s.motif !== motifFilter) return false;
      if (txt) {
        const hay = norm([
          s.ouvrier?.name, s.ouvrier?.cin, s.ouvrier?.qualification, chantierName,
          s.motif, s.date_stc, s.observation
        ].filter(Boolean).join(' '));
        if (!hay.includes(txt)) return false;
      }
      return true;
    });
  }, [stcs, chantierFilter, motifFilter, searchText]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'name':    va = a.ouvrier?.name || '';   vb = b.ouvrier?.name || '';   break;
        case 'chantier': va = a.ouvrier?.Chantier?.name || ''; vb = b.ouvrier?.Chantier?.name || ''; break;
        case 'total':   va = parseFloat(a.total || 0); vb = parseFloat(b.total || 0); break;
        default:        va = a[sortField] ?? '';  vb = b[sortField] ?? '';
      }
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  // Stats par motif calculées AVANT le filtre motif (pour afficher les counts même sans filtrer)
  const motifStats = useMemo(() => {
    const txt = norm(searchText);
    const base = stcs.filter(s => {
      const cid = Number(s.ouvrier?.chantier_id || 0);
      if (chantierFilter.length && !chantierFilter.includes(cid)) return false;
      if (txt) {
        const hay = norm([s.ouvrier?.name, s.ouvrier?.cin, s.ouvrier?.Chantier?.name, s.motif].filter(Boolean).join(' '));
        if (!hay.includes(txt)) return false;
      }
      return true;
    });
    const all = { count: base.length, total: base.reduce((s, r) => s + parseFloat(r.total || 0), 0) };
    const byMotif = {};
    Object.keys(MOTIF_LABELS).forEach(m => { byMotif[m] = { count: 0, total: 0 }; });
    base.forEach(s => {
      if (s.motif && byMotif[s.motif]) {
        byMotif[s.motif].count++;
        byMotif[s.motif].total += parseFloat(s.total || 0);
      }
    });
    return { all, ...byMotif };
  }, [stcs, chantierFilter, searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce STC ?')) return;
    try {
      await axios.delete(`/stc/${id}`);
      setSuccess('✅ STC supprimé.');
      fetchStcs();
    } catch {
      setError('❌ Erreur suppression.');
    }
  };

  // ── Export template vide ──
  const exportTemplate = () => {
    const template = [
      { 'ID OUVRIER': 1, 'Nom Prenom': 'Exemple Nom', 'CIN': 'AB123456', 'Projet': 'Chantier Alpha', 'Taux': 150, 'Juin': 26, 'Juillet': 26, 'Aout': 26, 'Septembre': 26, 'Octobre': 26, 'Novembre': 26, 'Décembre': 26, 'Janvier': 26, 'Février': 26, 'Mars': 26, 'Avril': 26, 'Total': 286, 'date stc': '15/01/2026', 'TOTAL STC': 5000, 'motif': 'licenciement', 'observation': '' },
      { 'ID OUVRIER': 2, 'Nom Prenom': 'Autre Ouvrier', 'CIN': 'CD789012', 'Projet': 'Chantier Beta', 'Taux': 120, 'Juin': 0, 'Juillet': 26, 'Aout': 26, 'Septembre': 26, 'Octobre': 0, 'Novembre': 26, 'Décembre': 26, 'Janvier': 26, 'Février': 26, 'Mars': 26, 'Avril': 26, 'Total': 234, 'date stc': '20/02/2026', 'TOTAL STC': 3200, 'motif': 'fin_chantier', 'observation': 'Accord signé' }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template STC');
    XLSX.writeFile(wb, 'template_import_stc.xlsx');
  };

  // ── Parse fichier Excel importé ──
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportErrors([]);
    setImportDone(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb  = XLSX.read(ev.target.result, { type: 'binary', cellDates: true });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

        // Normalise une clé de colonne : "ID OUVRIER" → "id_ouvrier"
        const norm = (s) => String(s).toLowerCase().replace(/[\s "'()]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');

        // Cherche la valeur parmi plusieurs alias possibles
        const getField = (r, ...aliases) => {
          for (const alias of aliases) {
            const a = norm(alias);
            const key = Object.keys(r).find(k => norm(k) === a);
            if (key != null && r[key] !== '' && r[key] != null) return r[key];
          }
          return '';
        };

        // Convertit une date Excel (objet Date, numéro série, ou string DD/MM/YYYY)
        const toDateStr = (v) => {
          if (!v && v !== 0) return '';
          if (v instanceof Date) return v.toISOString().split('T')[0];
          if (typeof v === 'number') {
            // Numéro de série Excel
            const d = new Date(Math.round((v - 25569) * 86400 * 1000));
            return d.toISOString().split('T')[0];
          }
          const s = String(v).trim();
          const parts = s.split('/');
          if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
          return s;
        };

        const MOIS = ['juin','juillet','aout','août','septembre','octobre','novembre','décembre','decembre','janvier','février','fevrier','mars','avril','mai'];

        const rows = raw.map((r, i) => {
          const id_ouvrier  = String(getField(r, 'id ouvrier', 'id_ouvrier', 'id')).trim();
          const nom         = String(getField(r, 'nom prenom', 'nom_prenom', 'nom', 'name', 'prenom')).trim();
          const cin         = String(getField(r, 'cin')).trim();
          const projet      = String(getField(r, 'projet', 'chantier', 'societe', 'dernier_chantier')).trim();
          const taux        = getField(r, 'taux', 'taux journalier', 'salary');
          const total_jours = getField(r, 'total', 'total jours', 'total_jours', 'nb jours');
          const date_stc    = toDateStr(getField(r, 'date stc', 'date_stc', 'date'));
          const montant_stc = String(getField(r, 'total stc', 'total_stc', 'montant stc', 'montant_stc', 'stc')).replace(/\s/g,'').replace(',','.').trim();
          const motif       = String(getField(r, 'motif')).trim();
          const observation = String(getField(r, 'observation', 'obs', 'remarque')).trim();

          // Colonnes mois (pour affichage uniquement)
          const moisVals = {};
          MOIS.forEach(m => { const v = getField(r, m); if (v !== '') moisVals[m] = v; });

          // Ignorer les lignes vides ou les lignes de total/entête parasites
          if (!id_ouvrier || id_ouvrier.toLowerCase().includes('total') || id_ouvrier.toLowerCase().includes('nom')) {
            return null;
          }

          return {
            _row: i + 2,
            id_ouvrier,
            nom,
            cin,
            projet,
            taux,
            total_jours,
            date_stc,
            montant_stc,
            motif,
            observation,
            moisVals,
            _valid: !!(id_ouvrier && date_stc && montant_stc && !isNaN(parseFloat(montant_stc)))
          };
        }).filter(Boolean);

        setImportRows(rows);
      } catch (err) {
        setImportErrors([{ row: '—', message: 'Fichier invalide : ' + err.message }]);
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // ── Soumettre l'import ──
  const handleImport = async () => {
    const validRows = importRows.filter(r => r._valid);
    if (!validRows.length) return;
    setImporting(true);
    setImportErrors([]);
    try {
      const { data } = await axios.post('/stc/import', validRows.map(r => ({
        id_ouvrier:  r.id_ouvrier,
        date_stc:    r.date_stc,
        montant_stc: r.montant_stc,
        motif:       r.motif || null,
        observation: r.observation || null
      })));
      setImportDone(data);
      if (data.errors?.length === 0) {
        setImportRows([]);
        fetchStcs();
      }
    } catch (err) {
      setImportErrors([{ row: '—', message: err.response?.data?.message || 'Erreur serveur' }]);
    } finally {
      setImporting(false);
    }
  };

  const exportExcel = () => {
    const rows = sorted.map(s => ({
      ID:              s.id,
      Date_STC:        s.date_stc ? String(s.date_stc).split('T')[0] : '',
      id_ouvrier:      s.ouvrier?.id || '',
      Ouvrier:         s.ouvrier?.name || '',
      CIN:             s.ouvrier?.cin || '',
      Qualification:   s.ouvrier?.qualification || '',
      Dernier_Chantier: s.ouvrier?.Chantier?.name || '',
      Societe:         s.ouvrier?.Chantier?.societe || '',
      Motif:         MOTIF_LABELS[s.motif] || s.motif || '',
      Salaire_du:    s.salaire_du,
      Conges:        s.indemnite_conge,
      Licenciement:  s.indemnite_licenciement,
      Preavis:       s.indemnite_preavis,
      Autres:        s.autres_indemnites,
      Total:         s.total,
      Observation:   s.observation || ''
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'STC');
    XLSX.writeFile(wb, `STC_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);
  };

  const SortIcon = ({ field }) => (
    <span style={{ opacity: sortField === field ? 1 : 0.2, marginLeft: 3, fontSize: '0.65em' }}>
      {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
    </span>
  );

  const TH = ({ field, children, style = {} }) => (
    <th
      onClick={() => field && toggleSort(field)}
      style={{
        padding: '9px 12px',
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#6d4e36',
        background: '#faf7f4',
        borderBottom: '2px solid rgba(109,78,54,0.18)',
        whiteSpace: 'nowrap',
        cursor: field ? 'pointer' : 'default',
        userSelect: 'none',
        ...style
      }}
    >
      {children}{field && <SortIcon field={field} />}
    </th>
  );

  return (
    <div className="container mt-4 mb-5 stc-page">

      {/* ── Header ── */}
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="m-0 stc-title">
            <FaReceipt className="me-2" />
            Registre des STC
          </h2>
          <div className="stc-subtitle">
            Solde de Tout Compte · tous les enregistrements
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={exportTemplate}>
            <FaFileExcel className="me-1" /> Template
          </Button>
          {canWrite && (
            <>
              <Button variant="outline-primary" size="sm" onClick={() => { setShowImport(true); setImportRows([]); setImportDone(null); setImportErrors([]); }}>
                <FaFileImport className="me-1" /> Importer Excel
              </Button>
              <input ref={importRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileChange} />
            </>
          )}
          <Button variant="outline-success" size="sm" onClick={exportExcel} disabled={sorted.length === 0}>
            <FaFileExcel className="me-1" /> Export
          </Button>
        </div>
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ── KPI + Filtres dates/chantier/recherche ── */}
      <Card className="stc-filter-card mb-3">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={2}>
              <Form.Label className="small fw-bold mb-1"><FaCalendarAlt className="me-1" />Début</Form.Label>
              <Form.Control type="date" size="sm" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
            </Col>
            <Col md={2}>
              <Form.Label className="small fw-bold mb-1">Fin</Form.Label>
              <Form.Control type="date" size="sm" value={dateFin} onChange={e => setDateFin(e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-bold mb-1">Chantier</Form.Label>
              <Select
                isMulti isClearable isSearchable
                options={chantierOptions}
                onChange={(opts) => {
                  const arr = opts || [];
                  const hasAll = arr.some(x => x.value === '__ALL__');
                  setChantierFilter(hasAll ? chantiers.map(c => c.id) : arr.map(x => x.value));
                }}
                placeholder="Tous..."
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }), control: b => ({ ...b, fontSize: '0.85rem', minHeight: 31 }) }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-bold mb-1"><FaSearch className="me-1" />Recherche</Form.Label>
              <InputGroup size="sm">
                <Form.Control value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Nom, CIN, chantier..." />
                {searchText && <Button variant="outline-secondary" onClick={() => setSearchText('')}>×</Button>}
              </InputGroup>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button variant="outline-secondary" size="sm" onClick={() => { setSearchText(''); setChantierFilter([]); setMotifFilter(''); }}>
                <FaBroom />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Filtres motif clicables ── */}
      <div className="stc-motif-cards mb-3">
        {/* Tous */}
        <button
          className={`stc-motif-card ${!motifFilter ? 'stc-motif-card--active' : ''}`}
          style={{ '--mc': '#6f42c1' }}
          onClick={() => setMotifFilter('')}
        >
          <div className="stc-mc-label">Tous</div>
          <div className="stc-mc-count">{motifStats.all.count} <span>STC</span></div>
          <div className="stc-mc-total">{motifStats.all.total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} DH</div>
        </button>

        {Object.entries(MOTIF_LABELS).map(([key, label]) => {
          const st   = MOTIF_STYLE[key] || MOTIF_STYLE.autre;
          const stat = motifStats[key] || { count: 0, total: 0 };
          const active = motifFilter === key;
          return (
            <button
              key={key}
              className={`stc-motif-card ${active ? 'stc-motif-card--active' : ''}`}
              style={{ '--mc': st.color }}
              onClick={() => setMotifFilter(active ? '' : key)}
            >
              <div className="stc-mc-label">{label}</div>
              <div className="stc-mc-count">{stat.count} <span>STC</span></div>
              <div className="stc-mc-total">{stat.total.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} DH</div>
            </button>
          );
        })}

        {/* Indicateur résultats */}
        <div className="stc-mc-result">
          <span>{sorted.length}</span>
          <small>résultat{sorted.length !== 1 ? 's' : ''}</small>
        </div>
      </div>

      {/* ── Tableau ── */}
      {loading ? (
        <div className="d-flex align-items-center gap-2 py-4">
          <Spinner animation="border" size="sm" /><span>Chargement...</span>
        </div>
      ) : (
        <Card className="stc-table-card">
          <Card.Body className="p-0">
            <div className="stc-table-scroll">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 36, padding: '9px 8px', background: '#faf7f4', borderBottom: '2px solid rgba(109,78,54,0.18)', textAlign: 'center' }}>
                      <input type="checkbox" checked={sorted.length > 0 && selectedIds.size === sorted.length} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                    </th>
                    <TH field="date_stc"  style={{ width: 4 }}></TH>
                    <TH field="date_stc"  style={{ width: 106 }}>Date</TH>
                    <TH field="name"      style={{ minWidth: 180 }}>Ouvrier</TH>
                    <TH field="chantier"  style={{ minWidth: 120 }}>Chantier</TH>
                    <TH field="motif"     style={{ width: 130 }}>Motif</TH>
                    <TH                   style={{ width: 100, textAlign: 'right' }}>Salaire dû</TH>
                    <TH                   style={{ width: 90,  textAlign: 'right' }}>Congés</TH>
                    <TH                   style={{ width: 110, textAlign: 'right' }}>Licenciement</TH>
                    <TH                   style={{ width: 90,  textAlign: 'right' }}>Préavis</TH>
                    <TH                   style={{ width: 80,  textAlign: 'right' }}>Autres</TH>
                    <TH field="total"     style={{ width: 120, textAlign: 'right' }}>Total</TH>
                    {canWrite && <TH style={{ width: 50, textAlign: 'center' }}></TH>}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="stc-empty">
                        <FaReceipt className="stc-empty-icon" />
                        <div>Aucun STC sur cette période</div>
                      </td>
                    </tr>
                  ) : sorted.map((s, i) => {
                    const ms = MOTIF_STYLE[s.motif] || MOTIF_STYLE.autre;
                    return (
                    <tr key={s.id} style={{ borderBottom: '1px solid rgba(109,78,54,0.07)', background: selectedIds.has(s.id) ? 'rgba(111,66,193,0.06)' : i % 2 === 0 ? '#fff' : '#fdfaf7' }}>
                      <td style={{ padding: '9px 8px', textAlign: 'center', borderBottom: `1px solid ${ms.border}` }}>
                        <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleSelect(s.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      {/* Bande couleur motif */}
                      <td style={{ padding: 0, width: 4, background: ms.color, borderBottom: `1px solid ${ms.border}` }} />
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: '#6f42c1', whiteSpace: 'nowrap' }}>
                        {s.date_stc ? String(s.date_stc).split('T')[0] : '—'}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        <div style={{ fontWeight: 600, color: '#2a1a0a', lineHeight: 1.2 }}>{s.ouvrier?.name || '—'}</div>
                        <div style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: '#999', marginTop: 1 }}>{s.ouvrier?.cin || ''}</div>
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {s.ouvrier?.Chantier?.name
                          ? <span className="stc-chip">{s.ouvrier.Chantier.name}</span>
                          : <span className="text-muted small">—</span>}
                      </td>
                      <td style={{ padding: '9px 12px' }}>
                        {s.motif
                          ? <span style={{ fontSize: '0.72rem', fontWeight: 700, color: ms.color, background: ms.bg, border: `1px solid ${ms.border}`, borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap' }}>
                              {MOTIF_LABELS[s.motif] || s.motif}
                            </span>
                          : <span className="text-muted small">—</span>}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#666', fontSize: '0.82rem' }}>{fmtDH(s.salaire_du)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#666', fontSize: '0.82rem' }}>{fmtDH(s.indemnite_conge)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#666', fontSize: '0.82rem' }}>{fmtDH(s.indemnite_licenciement)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#666', fontSize: '0.82rem' }}>{fmtDH(s.indemnite_preavis)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: '#666', fontSize: '0.82rem' }}>{fmtDH(s.autres_indemnites)}</td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 800, color: '#1b5e20', fontSize: '0.9rem' }}>
                        {fmtDH(s.total)}
                      </td>
                      {canWrite && (
                        <td style={{ padding: '9px 8px', textAlign: 'center' }}>
                          <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: '1px solid #f8d7da', borderRadius: 7, width: 28, height: 28, cursor: 'pointer', color: '#c62828', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Supprimer">
                            <FaTrash style={{ fontSize: '0.65rem' }} />
                          </button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
                {sorted.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f5ede4', borderTop: '2px solid rgba(109,78,54,0.2)' }}>
                      <td colSpan={5} style={{ padding: '9px 12px', fontWeight: 700, fontSize: '0.75rem', color: '#6d4e36', textTransform: 'uppercase' }}>
                        Total · {sorted.length} STC
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.salaire_du || 0), 0))}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.indemnite_conge || 0), 0))}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.indemnite_licenciement || 0), 0))}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.indemnite_preavis || 0), 0))}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700 }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.autres_indemnites || 0), 0))}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 900, color: '#2e7d32', fontSize: '0.95rem' }}>
                        {fmtDH(sorted.reduce((s, r) => s + parseFloat(r.total || 0), 0))}
                      </td>
                      {canWrite && <td />}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card.Body>
        </Card>
      )}
      {/* ── Barre d'actions de masse ── */}
      {selectedIds.size > 0 && canWrite && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1040, background: '#1e1b2e', borderRadius: 16, padding: '12px 20px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center',
          gap: 10, flexWrap: 'wrap', minWidth: 420, maxWidth: '90vw'
        }}>
          {/* Compteur */}
          <span style={{ color: '#c8b8f0', fontWeight: 700, fontSize: '0.85rem', marginRight: 4, whiteSpace: 'nowrap' }}>
            {selectedIds.size} STC sélectionné{selectedIds.size > 1 ? 's' : ''}
          </span>

          {/* Choix action */}
          <select
            value={bulkAction}
            onChange={e => { setBulkAction(e.target.value); setBulkMotif(''); setBulkDate(''); }}
            style={{ borderRadius: 8, border: '1.5px solid #6f42c1', background: '#2a2040', color: '#e0d0ff', padding: '5px 10px', fontSize: '0.82rem', cursor: 'pointer' }}
          >
            <option value="">-- Choisir une action --</option>
            <option value="motif">Changer le motif</option>
            <option value="date">Changer la date STC</option>
            <option value="recalculate">Recalculer le montant (pointages)</option>
            <option value="delete">Supprimer</option>
          </select>

          {/* Options selon action */}
          {bulkAction === 'motif' && (
            <select value={bulkMotif} onChange={e => setBulkMotif(e.target.value)}
              style={{ borderRadius: 8, border: '1.5px solid #6f42c1', background: '#2a2040', color: '#e0d0ff', padding: '5px 10px', fontSize: '0.82rem' }}>
              <option value="">-- Motif --</option>
              {Object.entries(MOTIF_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}
          {bulkAction === 'date' && (
            <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)}
              style={{ borderRadius: 8, border: '1.5px solid #6f42c1', background: '#2a2040', color: '#e0d0ff', padding: '5px 10px', fontSize: '0.82rem' }} />
          )}
          {bulkAction === 'recalculate' && (
            <span style={{ fontSize: '0.75rem', color: '#9a7ac8', fontStyle: 'italic' }}>
              Somme jours pointés × formule STC
            </span>
          )}

          {/* Boutons */}
          <button
            onClick={handleBulkAction}
            disabled={bulkLoading || !bulkAction || (bulkAction === 'motif' && !bulkMotif) || (bulkAction === 'date' && !bulkDate)}
            style={{
              borderRadius: 10, border: 'none', padding: '7px 18px', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
              background: bulkAction === 'delete' ? '#c62828' : '#6f42c1', color: '#fff',
              opacity: (bulkLoading || !bulkAction || (bulkAction === 'motif' && !bulkMotif) || (bulkAction === 'date' && !bulkDate)) ? 0.5 : 1
            }}
          >
            {bulkLoading ? '...' : bulkAction === 'delete' ? 'Supprimer' : 'Appliquer'}
          </button>
          <button onClick={() => { setSelectedIds(new Set()); setBulkAction(''); }}
            style={{ borderRadius: 10, border: '1.5px solid #555', padding: '7px 14px', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', background: 'transparent', color: '#aaa' }}>
            ✕
          </button>
        </div>
      )}

      {/* ── Modal Import Excel ── */}
      {showImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.18)' }}>

            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1.5px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaFileImport style={{ color: '#6f42c1' }} /> Import STC depuis Excel
              </div>
              <button onClick={() => setShowImport(false)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#999' }}>×</button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

              {/* Zone upload */}
              {importRows.length === 0 && !importDone && (
                <div
                  onClick={() => importRef.current?.click()}
                  style={{ border: '2px dashed #c8b8f0', borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: '#f9f6ff', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0e8ff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f9f6ff'}
                >
                  <FaFileExcel style={{ fontSize: '2.5rem', color: '#6f42c1', opacity: 0.5, marginBottom: 10 }} />
                  <div style={{ fontWeight: 700, color: '#6f42c1', marginBottom: 4 }}>Cliquer pour sélectionner un fichier</div>
                  <div style={{ fontSize: '0.78rem', color: '#aaa' }}>.xlsx · .xls · colonnes : id_ouvrier, nom, date_stc, montant_stc, motif, observation</div>
                </div>
              )}

              {/* Résultat import */}
              {importDone && (
                <Alert variant={importDone.errors?.length === 0 ? 'success' : 'warning'} className="mb-3">
                  <FaCheckCircle className="me-2" />
                  <strong>{importDone.imported}</strong> STC importé{importDone.imported !== 1 ? 's' : ''}
                  {importDone.errors?.length > 0 && <span className="ms-2">· <strong>{importDone.errors.length}</strong> erreur{importDone.errors.length !== 1 ? 's' : ''}</span>}
                </Alert>
              )}

              {/* Erreurs parsing/backend */}
              {importErrors.map((e, i) => (
                <Alert key={i} variant="danger" className="py-2 mb-2">
                  <FaExclamationTriangle className="me-2" /> Ligne {e.row} : {e.message}
                </Alert>
              ))}

              {/* Preview table */}
              {importRows.length > 0 && !importDone && (
                <>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 8 }}>
                    <strong>{importRows.filter(r => r._valid).length}</strong> ligne{importRows.filter(r => r._valid).length !== 1 ? 's' : ''} valide{importRows.filter(r => r._valid).length !== 1 ? 's' : ''} sur {importRows.length}
                    {importRows.some(r => !r._valid) && <span style={{ color: '#c62828', marginLeft: 8 }}>· {importRows.filter(r => !r._valid).length} invalide{importRows.filter(r => !r._valid).length !== 1 ? 's' : ''} (ignorées)</span>}
                  </div>
                  <div style={{ overflowX: 'auto', border: '1px solid #e0d8f8', borderRadius: 8, maxHeight: 360 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem' }}>
                      <thead>
                        <tr style={{ background: '#f4f0ff', position: 'sticky', top: 0 }}>
                          {['#', 'ID', 'Nom', 'CIN', 'Projet', 'Taux', 'Jours', 'Date STC', 'TOTAL STC', 'Motif', ''].map(h => (
                            <th key={h} style={{ padding: '7px 10px', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', color: '#6f42c1', whiteSpace: 'nowrap', borderBottom: '1.5px solid #d8c8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.map((r, i) => (
                          <tr key={i} style={{ background: r._valid ? (i % 2 === 0 ? '#fff' : '#fdfaff') : '#fff5f5', opacity: r._valid ? 1 : 0.6 }}>
                            <td style={{ padding: '5px 10px', color: '#aaa', fontSize: '0.7rem' }}>{r._row}</td>
                            <td style={{ padding: '5px 10px', fontWeight: 700 }}>{r.id_ouvrier || <span style={{ color: '#c62828' }}>—</span>}</td>
                            <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>{r.nom || '—'}</td>
                            <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.cin || '—'}</td>
                            <td style={{ padding: '5px 10px', fontSize: '0.75rem' }}>{r.projet || '—'}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', color: '#555' }}>{r.taux || '—'}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 600, color: '#1565c0' }}>{r.total_jours || '—'}</td>
                            <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.date_stc || <span style={{ color: '#c62828' }}>—</span>}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>
                              {r.montant_stc ? Number(r.montant_stc).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) : <span style={{ color: '#c62828' }}>—</span>}
                            </td>
                            <td style={{ padding: '5px 10px', fontSize: '0.72rem' }}>{r.motif || '—'}</td>
                            <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                              {r._valid
                                ? <FaCheckCircle style={{ color: '#2e7d32', fontSize: '0.85rem' }} />
                                : <FaExclamationTriangle style={{ color: '#c62828', fontSize: '0.85rem' }} title="id_ouvrier, date_stc ou montant manquant" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Button variant="outline-secondary" size="sm" onClick={() => { setImportRows([]); setImportDone(null); setImportErrors([]); importRef.current && (importRef.current.value = ''); }}>
                {importDone ? 'Fermer' : 'Annuler'}
              </Button>
              {importRows.length > 0 && !importDone && (
                <Button
                  variant="primary" size="sm"
                  disabled={importing || importRows.filter(r => r._valid).length === 0}
                  onClick={handleImport}
                >
                  {importing ? <Spinner animation="border" size="sm" className="me-1" /> : <FaFileImport className="me-1" />}
                  Importer {importRows.filter(r => r._valid).length} ligne{importRows.filter(r => r._valid).length !== 1 ? 's' : ''}
                </Button>
              )}
              {!importRows.length && !importDone && (
                <Button variant="primary" size="sm" onClick={() => importRef.current?.click()}>
                  <FaFileExcel className="me-1" /> Choisir un fichier
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StcPage;
